import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { fileResponseNamesNoImage } from "@rivets/core";
import { useFileImageSource, useWorktreeFile, worktreeFileQuery } from "@/features/files/CodeViewer";
import { qk } from "@/lib/query-keys";
import { resolveWorkspacePath, type FileLinkTarget } from "./fileLinks";
import { ImageLightbox, LightboxIcon } from "./ImageLightbox";
import { steppedIndex } from "./lightboxZoom";
import type { MessageAttachment } from "./messageAttachments";

// Images an agent saved and named in its answer are previewed under it and
// open in a lightbox over the chat, rather than in a file tab the way a path
// it merely cites does. Swift twin: `ImageLightboxController`.

export interface AgentImageViewer {
  /** The worktree the images are read from. */
  worktreeId: string;
  open: (images: readonly MessageAttachment[], index: number) => void;
}

/** Only a workspace has a worktree to read images from, so outside one this is
 * null and an answer's image paths stay in its text. */
export const AgentImageContext = createContext<AgentImageViewer | null>(null);

export function useAgentImageViewer() {
  return useContext(AgentImageContext);
}

/** The images of the answer being rendered, so a link in it that names one
 * opens the lightbox like the image's preview does. */
export const MessageImagesContext = createContext<readonly MessageAttachment[]>([]);

/** The worktree-relative path to read an agent's image from. The worker reads
 * nothing else; an absolute path is placed by its `.context` folder, where
 * agents save images. */
export function agentImageReadPath(path: string): string | null {
  return resolveWorkspacePath(path, null);
}

/** One agent image read out of the worktree as a data URL. Shared with the
 * file tab's query, so a preview, the lightbox and the tab read it once. */
export function useAgentImage(worktreeId: string, path: string): { src: string | null; failed: boolean } {
  const readPath = agentImageReadPath(path);
  const file = useWorktreeFile(worktreeId, readPath ?? path, readPath !== null);
  const src = useFileImageSource(readPath ? file.data : undefined);
  return { src, failed: !src && (!readPath || file.isError || file.isSuccess) };
}

/** How long an image the worker said is not there waits before it is asked
 * for once more. Swift twin: `WorktreeImageStore.missRetryInterval`. */
export const ABSENT_IMAGE_RECHECK_MS = 20_000;

/**
 * An answer's images, less those the worker says are not in the worktree.
 * Agents mention files they did not produce — a logo outside the worktree, a
 * file on another branch — and those stay in the text rather than becoming a
 * placeholder that opens to "not available". An image that could not be read
 * for now (an offline worker) is kept. One left out is asked for once more
 * after `ABSENT_IMAGE_RECHECK_MS`, since an agent may name a screenshot
 * before it takes it. Swift twin: `AgentImageAvailability`.
 */
export function usePresentAgentImages(images: readonly MessageAttachment[]): readonly MessageAttachment[] {
  const worktreeId = useAgentImageViewer()?.worktreeId ?? null;
  const queryClient = useQueryClient();
  const readPaths = worktreeId ? images.map((image) => agentImageReadPath(image.path)) : [];
  const reads = useQueries({
    queries: worktreeId
      ? readPaths.flatMap((readPath) => (readPath ? [worktreeFileQuery(worktreeId, readPath)] : []))
      : [],
  });
  let next = 0;
  const absent = worktreeId
    ? images.flatMap((image, index) => {
      if (readPaths[index] === null) return [image.path];
      const data = reads[next++]?.data;
      return data && fileResponseNamesNoImage(data) ? [image.path] : [];
    })
    : [];
  const absentKey = absent.join("\n");

  useEffect(() => {
    if (!worktreeId || !absentKey) return;
    const timer = setTimeout(() => {
      for (const path of absentKey.split("\n")) {
        const readPath = agentImageReadPath(path);
        if (readPath) void queryClient.invalidateQueries({ queryKey: qk.file(worktreeId, readPath), exact: true });
      }
    }, ABSENT_IMAGE_RECHECK_MS);
    return () => clearTimeout(timer);
  }, [worktreeId, absentKey, queryClient]);

  return useMemo(() => {
    if (!absentKey) return images;
    const left = new Set(absentKey.split("\n"));
    return images.filter((image) => !left.has(image.path));
  }, [images, absentKey]);
}

/**
 * The images whose preview can be drawn now: those whose read has answered —
 * an image, or an error that says it cannot be read for now. A preview drawn
 * while its read was still out put a tile under the answer, and when the
 * answer was "no image" took it away again a moment later: the transcript grew
 * by a tile and shrank straight back under the reader. Held back, a tile only
 * ever arrives. Links in the text still open every image in the lightbox.
 * Swift twin: `AgentImageAvailability.isSettled`.
 */
export function useSettledAgentImages(images: readonly MessageAttachment[]): readonly MessageAttachment[] {
  const worktreeId = useAgentImageViewer()?.worktreeId ?? null;
  const readPaths = worktreeId ? images.map((image) => agentImageReadPath(image.path)) : [];
  const reads = useQueries({
    queries: worktreeId
      ? readPaths.flatMap((readPath) => (readPath ? [worktreeFileQuery(worktreeId, readPath)] : []))
      : [],
  });
  if (!worktreeId) return images;
  let next = 0;
  const settled = images.filter((_, index) => {
    if (readPaths[index] === null) return true;
    const read = reads[next++];
    return !read || read.isSuccess || read.isError;
  });
  return settled.length === images.length ? images : settled;
}

interface Session {
  images: readonly MessageAttachment[];
  index: number;
}

/**
 * Holds the lightbox for a workspace's chat and draws it over the chat. Its
 * children open it through `AgentImageContext`. A new task or chat closes it:
 * the images belong to the answer they were opened from.
 */
export function AgentImageProvider({ worktreeId, chatId, openFile, children }: {
  worktreeId: string;
  chatId?: string | null;
  openFile: ((target: FileLinkTarget, inNewTab: boolean) => void) | null;
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const open = useCallback((images: readonly MessageAttachment[], index: number) => {
    if (images.length > 0) setSession({ images, index: steppedIndex(index, 0, images.length) });
  }, []);
  const close = useCallback(() => setSession(null), []);
  const viewer = useMemo(() => ({ worktreeId, open }), [worktreeId, open]);
  useEffect(() => setSession(null), [worktreeId, chatId]);

  return (
    <AgentImageContext.Provider value={viewer}>
      {children}
      {session && (
        <AgentImageLightbox
          worktreeId={worktreeId}
          session={session}
          onStep={(delta) => setSession((previous) => previous && { ...previous, index: steppedIndex(previous.index, delta, previous.images.length) })}
          onClose={close}
          openFile={openFile}
        />
      )}
    </AgentImageContext.Provider>
  );
}

function AgentImageLightbox({ worktreeId, session, onStep, onClose, openFile }: {
  worktreeId: string;
  session: Session;
  onStep: (delta: number) => void;
  onClose: () => void;
  openFile: ((target: FileLinkTarget, inNewTab: boolean) => void) | null;
}) {
  const image = session.images[session.index] ?? session.images[0]!;
  const { src, failed } = useAgentImage(worktreeId, image.path);
  return (
    <ImageLightbox
      contained
      src={src}
      failed={failed}
      alt={image.name}
      caption={image.name}
      position={{ index: session.index, count: session.images.length, onStep }}
      onClose={onClose}
      actions={openFile && (
        <button
          type="button"
          className="lightbox-button"
          aria-label="Open in tab"
          title="Open in tab"
          onClick={() => {
            onClose();
            openFile({ path: image.path, line: null, endLine: null }, false);
          }}
        >
          <LightboxIcon d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
        </button>
      )}
    />
  );
}
